import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import useToast from "../../hooks/useToast";
import { TOAST } from "../../constants/ToastMessages";

import BottomActionBar from "../../components/BottomActionBar";
import CodeEditor from "../../components/CodeEditor";
import HttpCapabilityConfiguration from "../../components/HttpCapabilityConfiguration";
import JsonEditor from "../../components/JsonEditor";
import LoadingSpinner from "../../components/LoadingSpinner";
import useApiCall from "../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../constants/Colors";
import { usePageMeta } from "../../contexts/PageMetaContext";
import TrimTextField from "../../components/TrimTextField";
import {
  CAPABILITY_TYPES,
  CAPABILITY_STATUSES,
  validateCapabilityConfiguration,
} from "./CapabilitiesStore";

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const FIELD_HINT_SX = {
  fontSize: "0.75rem",
  color: SLATE[400],
  mb: "6px",
  display: "block",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};



function SectionHeader({ title, description }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900] }}>
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], mt: "2px" }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

const DEFAULT_INPUT_SCHEMA = [{ field_name: "param_name", field_type: "string" }];
const DEFAULT_OUTPUT_SCHEMA = [{ field_name: "result", field_type: "string" }];

const INITIAL_STATE = {
  name: "",
  display_name: "",
  description: "",
  capability_type: "",
  status: "active",
  configuration: {
    tools_input: [{ key: "eg. order_id", key_type: "string", default: "" }],
  },
  input_schema: DEFAULT_INPUT_SCHEMA,
  output_schema: DEFAULT_OUTPUT_SCHEMA,
  before_capability_code: "",
  after_capability_code: "",
};

export default function ManageCapability() {
  const navigate = useNavigate();
  const { id } = useParams();
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { error } = useToast();
  const { setPageMeta } = usePageMeta();
  const isEdit = Boolean(id);

  const originalDataRef = useRef(INITIAL_STATE);
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [saving, setSaving] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [configErrors, setConfigErrors] = useState([]);
  const [jsonErrors, setJsonErrors] = useState({ headers: null, body: null, output_schema: null, input_schema: null, schema_output: null });

  useEffect(() => {
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEdit) {
      setPageMeta({ description: "Create a reusable capability template that can be attached to agents." });
    }
  }, [isEdit]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (!isEdit) return;
    setFetchLoading(true);
    makeRequest(`/v2/capabilities/${id}`, { method: "GET", authTokens: tokens })
      .then((res) => {
        const cap = res.capability || res;
        const configuration = cap.configuration || {};
        if (!configuration.tools_input || (Array.isArray(configuration.tools_input) && configuration.tools_input.length === 0)) {
          configuration.tools_input = [{ key: "eg. order_id", key_type: "string", default: "" }];
        }
        setFormData({
          name: cap.name || "",
          display_name: cap.display_name || "",
          description: cap.description || "",
          capability_type: cap.capability_type || "",
          status: cap.status || "active",
          configuration,
          input_schema: cap.input_schema ?? DEFAULT_INPUT_SCHEMA,
          output_schema: cap.output_schema ?? DEFAULT_OUTPUT_SCHEMA,
          before_capability_code: cap.before_capability_code || "",
          after_capability_code: cap.after_capability_code || "",
        });
        setPageMeta({ itemName: cap.display_name || cap.name, description: "Configure the capability's settings and behavior." });
        originalDataRef.current = {
          name: cap.name || "",
          display_name: cap.display_name || "",
          description: cap.description || "",
          capability_type: cap.capability_type || "",
          status: cap.status || "active",
          configuration,
          input_schema: cap.input_schema ?? DEFAULT_INPUT_SCHEMA,
          output_schema: cap.output_schema ?? DEFAULT_OUTPUT_SCHEMA,
          before_capability_code: cap.before_capability_code || "",
          after_capability_code: cap.after_capability_code || "",
        };
      })
      .catch(() => {})
      .finally(() => setFetchLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toSlug = (str) =>
    str.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  const handleChange = (field, value) => {
    if (field === "display_name" && !isEdit) {
      setFormData((prev) => ({ ...prev, display_name: value, name: toSlug(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "capability_type" && value !== formData.capability_type) {
      const httpDefaults = ["http_call", "rag_api_tool"].includes(value)
        ? { method: "GET", headers: { "Content-Type": "application/json" } }
        : {};
      setFormData((prev) => ({
        ...prev,
        capability_type: value,
        configuration: { tools_input: [{ key: "eg. order_id", key_type: "string", default: "" }], ...httpDefaults },
      }));
      setConfigErrors([]);
    }
  };

  const handleConfigChange = (field, value) => {
    setFormData((prev) => ({ ...prev, configuration: { ...prev.configuration, [field]: value } }));
  };

  const looksLikeJSON = (value) => {
    if (!value) return false;
    const trimmed = value.trim();
    return trimmed.startsWith("{") || trimmed.startsWith("[");
  };

  const handleHttpConfigChange = (field, value) => {
    if (["headers", "body", "output_schema"].includes(field)) {
      if (value === "" || value === null || value === undefined) {
        setJsonErrors((prev) => ({ ...prev, [field]: null }));
        handleConfigChange(field, value);
        return;
      }
      const shouldValidate = field === "output_schema" ? looksLikeJSON(value) : true;
      if (!shouldValidate) {
        setJsonErrors((prev) => ({ ...prev, [field]: null }));
        handleConfigChange(field, value);
        return;
      }
      try {
        const parsed = JSON.parse(value);
        setJsonErrors((prev) => ({ ...prev, [field]: null }));
        handleConfigChange(field, parsed);
      } catch (e) {
        setJsonErrors((prev) => ({ ...prev, [field]: e.message }));
        handleConfigChange(field, value);
      }
    } else {
      handleConfigChange(field, value);
    }
  };

  const getHttpConfigForDisplay = () => {
    const config = { ...formData.configuration };
    if (config.headers && typeof config.headers === "object") config.headers = JSON.stringify(config.headers, null, 2);
    else if (!config.headers) config.headers = "";
    if (config.body && typeof config.body === "object") config.body = JSON.stringify(config.body, null, 2);
    else if (!config.body) config.body = "";
    if (config.output_schema && typeof config.output_schema === "object") config.output_schema = JSON.stringify(config.output_schema, null, 2);
    else if (!config.output_schema) config.output_schema = "";
    return config;
  };

  const handleSave = async () => {
    if (!formData.capability_type) { error(TOAST.capability.typeRequired); return; }
    if (jsonErrors.headers || jsonErrors.body || jsonErrors.output_schema || jsonErrors.input_schema || jsonErrors.schema_output) {
      error(TOAST.capability.jsonValidationError);
      return;
    }
    const validation = validateCapabilityConfiguration(formData.capability_type, formData.configuration);
    if (!validation.valid) {
      setConfigErrors(validation.errors);
      error(TOAST.capability.configError(validation.errors));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        display_name: formData.display_name,
        description: formData.description,
        capability_type: formData.capability_type,
        status: formData.status,
        configuration: formData.configuration,
        input_schema: formData.input_schema,
        output_schema: formData.output_schema,
        before_capability_code: formData.before_capability_code,
        after_capability_code: formData.after_capability_code,
      };

      if (isEdit) {
        await makeRequest(`/v2/capabilities/${id}`, { method: "PUT", body: { capability: payload }, authTokens: tokens });
      } else {
        await makeRequest("/v2/capabilities", { method: "POST", body: { capability: payload }, authTokens: tokens });
      }
      navigate("/admin/capabilities");
    } catch {
      // error already toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  const renderConfigurationFields = () => {
    switch (formData.capability_type) {
      case "http_call":
        return (
          <HttpCapabilityConfiguration
            config={getHttpConfigForDisplay()}
            onChange={handleHttpConfigChange}
            useAccordions={false}
            showDescription={false}
            jsonErrors={jsonErrors}
          />
        );

      case "rag_api_tool":
        return (
          <>
            <Divider sx={{ my: 2.5, borderColor: SLATE[100] }} />
            <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], mb: 2 }}>HTTP Configuration for RAG Endpoint:</Typography>
            <HttpCapabilityConfiguration
              config={getHttpConfigForDisplay()}
              onChange={handleHttpConfigChange}
              useAccordions={false}
              showDescription={false}
              jsonErrors={jsonErrors}
            />
          </>
        );

      case "mcp_client":
        return (
          <>
            <Box sx={{ mb: 2.5 }}>
              <Typography component="label" sx={FIELD_LABEL_SX}>MCP Server URL <Box component="span" sx={{ color: "#ef4444" }}>*</Box></Typography>
              <TrimTextField
                fullWidth
                size="small"
                required
                value={formData.configuration.mcp_server_url || ""}
                onChange={(e) => handleConfigChange("mcp_server_url", e.target.value)}
                placeholder="http://localhost:3000"
                sx={INPUT_SX}
                inputProps={{
                  onInvalid: (e) => e.target.setCustomValidity("Please enter the MCP server URL"),
                  onInput: (e) => e.target.setCustomValidity(""),
                }}
              />
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography component="label" sx={FIELD_LABEL_SX}>MCP Capability Name <Box component="span" sx={{ color: "#ef4444" }}>*</Box></Typography>
              <TrimTextField
                fullWidth
                size="small"
                required
                value={formData.configuration.mcp_tool_name || ""}
                onChange={(e) => handleConfigChange("mcp_tool_name", e.target.value)}
                placeholder="get_weather"
                sx={INPUT_SX}
                inputProps={{
                  onInvalid: (e) => e.target.setCustomValidity("Please enter the MCP tool name"),
                  onInput: (e) => e.target.setCustomValidity(""),
                }}
              />
            </Box>
          </>
        );

      case "llm_tool":
        return (
          <>
            <Box sx={{ mb: 2.5 }}>
              <Typography component="label" sx={FIELD_LABEL_SX}>Payload (JSON)</Typography>
              <Typography sx={FIELD_HINT_SX}>Default payload structure for LLM capability calls</Typography>
              <JsonEditor
                value={
                  typeof formData.configuration.payload === "object"
                    ? JSON.stringify(formData.configuration.payload, null, 2)
                    : formData.configuration.payload || ""
                }
                onChange={(newString) => {
                  try {
                    const parsed = newString ? JSON.parse(newString) : {};
                    handleConfigChange("payload", parsed);
                  } catch {
                    handleConfigChange("payload", newString);
                  }
                }}
                height="160px"
              />
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography component="label" sx={FIELD_LABEL_SX}>Output Format (JSON)</Typography>
              <Typography sx={FIELD_HINT_SX}>Expected output format or schema for the LLM response</Typography>
              <JsonEditor
                value={
                  typeof formData.configuration.output_format === "object"
                    ? JSON.stringify(formData.configuration.output_format, null, 2)
                    : formData.configuration.output_format || ""
                }
                onChange={(newString) => {
                  try {
                    const parsed = newString ? JSON.parse(newString) : {};
                    handleConfigChange("output_format", parsed);
                  } catch {
                    handleConfigChange("output_format", newString);
                  }
                }}
                height="160px"
              />
            </Box>
          </>
        );

      default:
        return (
          <Typography sx={{ fontSize: "0.875rem", color: SLATE[400] }}>
            Select a capability type to configure
          </Typography>
        );
    }
  };

  if (fetchLoading) {
    return <LoadingSpinner message="Loading capability..." />;
  }

  return (
    <Box
      component="form"
      onSubmit={(e) => { e.preventDefault(); handleSave(); }}
      sx={{ width: "100%", maxWidth: 720, pb: 10 }}
    >
      {/* Basic Information */}
      <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", p: 3, mb: 2.5 }}>
        <SectionHeader title="Basic Information" description="Set the capability's name, type, and status." />

        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            Display Name <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
          </Typography>
          <TrimTextField
            fullWidth
            size="small"
            required
            placeholder="e.g. Weather API Call"
            value={formData.display_name}
            onChange={(e) => handleChange("display_name", e.target.value)}
            sx={INPUT_SX}
            inputProps={{
              onInvalid: (e) => e.target.setCustomValidity("Please enter a display name"),
              onInput: (e) => e.target.setCustomValidity(""),
            }}
          />
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            Capability Name <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            <Typography component="span" sx={{ fontSize: "0.75rem", color: SLATE[400], ml: 1 }}>
              {isEdit ? "(cannot be changed after creation)" : "(auto-generated)"}
            </Typography>
          </Typography>
          <Typography sx={FIELD_HINT_SX}>Unique identifier — lowercase letters and underscores only</Typography>
          <TrimTextField
            fullWidth
            size="small"
            required
            placeholder="e.g. weather_api_call"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            disabled={isEdit}
            sx={INPUT_SX}
            inputProps={{
              onInvalid: (e) => e.target.setCustomValidity("Please enter a capability name"),
              onInput: (e) => e.target.setCustomValidity(""),
            }}
          />
        </Box>

        {formData.capability_type && (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5, mb: 2.5 }}>
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>Tool Input Schema (JSON)</Typography>
              <Typography sx={FIELD_HINT_SX}>Array of fields this tool accepts — e.g. field_name, field_type</Typography>
              <JsonEditor
                value={
                  Array.isArray(formData.input_schema) || typeof formData.input_schema === "object"
                    ? JSON.stringify(formData.input_schema, null, 2)
                    : formData.input_schema || ""
                }
                onChange={(newString) => {
                  try {
                    const parsed = newString ? JSON.parse(newString) : [];
                    setJsonErrors((prev) => ({ ...prev, input_schema: null }));
                    setFormData((prev) => ({ ...prev, input_schema: parsed }));
                  } catch (e) {
                    setJsonErrors((prev) => ({ ...prev, input_schema: e.message }));
                    setFormData((prev) => ({ ...prev, input_schema: newString }));
                  }
                }}
                height="160px"
              />
              {jsonErrors.input_schema && (
                <Typography sx={{ fontSize: "0.75rem", color: "#ef4444", mt: "4px" }}>
                  Invalid JSON: {jsonErrors.input_schema}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>Tool Output Schema (JSON)</Typography>
              <Typography sx={FIELD_HINT_SX}>Array of fields this tool returns — e.g. field_name, field_type</Typography>
              <JsonEditor
                value={
                  Array.isArray(formData.output_schema) || typeof formData.output_schema === "object"
                    ? JSON.stringify(formData.output_schema, null, 2)
                    : formData.output_schema || ""
                }
                onChange={(newString) => {
                  try {
                    const parsed = newString ? JSON.parse(newString) : [];
                    setJsonErrors((prev) => ({ ...prev, schema_output: null }));
                    setFormData((prev) => ({ ...prev, output_schema: parsed }));
                  } catch (e) {
                    setJsonErrors((prev) => ({ ...prev, schema_output: e.message }));
                    setFormData((prev) => ({ ...prev, output_schema: newString }));
                  }
                }}
                height="160px"
              />
              {jsonErrors.schema_output && (
                <Typography sx={{ fontSize: "0.75rem", color: "#ef4444", mt: "4px" }}>
                  Invalid JSON: {jsonErrors.schema_output}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>Description</Typography>
          <TrimTextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Describe what this capability does..."
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            sx={INPUT_SX}
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Capability Type <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
              {isEdit && (
                <Typography component="span" sx={{ fontSize: "0.75rem", color: SLATE[400], ml: 1 }}>
                  (cannot change)
                </Typography>
              )}
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              required
              value={formData.capability_type}
              onChange={(e) => handleChange("capability_type", e.target.value)}
              disabled={isEdit}
              sx={INPUT_SX}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Select Type</MenuItem>
              {CAPABILITY_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{type.label}</Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: SLATE[500] }}>{type.description}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Status</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={formData.status}
              onChange={(e) => handleChange("status", e.target.value)}
              sx={INPUT_SX}
            >
              {CAPABILITY_STATUSES.map((s) => (
                <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </Box>

      {/* Configuration */}
      {formData.capability_type && (
        <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", p: 3, mb: 2.5 }}>
          <SectionHeader title="Configuration" description="Configure the capability's endpoint and behavior." />
          {renderConfigurationFields()}

          <Divider sx={{ my: 2.5, borderColor: SLATE[100] }} />

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Capability Input Arguments (JSON)</Typography>
            <Typography sx={FIELD_HINT_SX}>Define the input parameters this capability accepts</Typography>
            <JsonEditor
              value={
                typeof formData.configuration.tools_input === "object"
                  ? JSON.stringify(formData.configuration.tools_input, null, 2)
                  : formData.configuration.tools_input || ""
              }
              onChange={(newString) => {
                try {
                  const parsed = newString ? JSON.parse(newString) : {};
                  handleConfigChange("tools_input", parsed);
                } catch {
                  handleConfigChange("tools_input", newString);
                }
              }}
              height="180px"
            />
          </Box>

          {configErrors.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px" }}>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#b91c1c", mb: 0.5 }}>
                Configuration Errors:
              </Typography>
              {configErrors.map((err, idx) => (
                <Typography key={idx} sx={{ fontSize: "0.8125rem", color: "#b91c1c" }}>• {err}</Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Advanced */}
      <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", p: 3, mb: 2.5 }}>
        <SectionHeader title="Advanced" description="Optional pre/post-processing hooks for this capability." />

        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>Before Capability Code (Python)</Typography>
          <Typography sx={FIELD_HINT_SX}>Runs before the capability executes — use to preprocess inputs or inject context</Typography>
          <CodeEditor
            value={formData.before_capability_code}
            onChange={(val) => handleChange("before_capability_code", val)}
            language="python"
            placeholder="# e.g. sanitize or transform inputs before execution"
          />
        </Box>

        <Box>
          <Typography component="label" sx={FIELD_LABEL_SX}>After Capability Code (Python)</Typography>
          <Typography sx={FIELD_HINT_SX}>Runs after the capability executes — use to transform or validate the response</Typography>
          <CodeEditor
            value={formData.after_capability_code}
            onChange={(val) => handleChange("after_capability_code", val)}
            language="python"
            placeholder="# e.g. parse, filter or reshape the response"
          />
        </Box>
      </Box>

      <BottomActionBar
        name={isEdit ? "Save Capability" : "Create Capability"}
        cancelRedirectUrl="/admin/capabilities"
        saving={saving}
        isDirty={JSON.stringify(formData) !== JSON.stringify(originalDataRef.current)}
      />
    </Box>
  );
}
